import { ExtensionWorkflowComponent } from './components/common/extensioncomponent'
import { StoredProcedureWorkflowComponent } from './components/common/storedprocedurecomponent'
import { Workflow } from './workflow'
import { DataWarehouseConnection } from './workflow/connections'

export enum Provider {
  BigQuery = 'bigquery',
  Redshift = 'redshift',
  Postgres = 'postgres',
  Snowflake = 'snowflake',
  DatabricksRest = 'databricksRest',
  Oracle = 'oracle'
  //Databricks = 'databricks'
}

export enum Language {
  SQL = 'sql',
  Python = 'python'
}

export enum TokenAllowedApis {
  maps = 'maps',
  sql = 'sql',
  imports = 'imports',
  lds = 'lds',
  workflows = 'workflows',
  batch = 'batch',
  httpRequest = 'httpRequest',
  resources = 'resources'
}

export type Grant =
  | {
      connection_name: string
      source: string
    }
  | {
      host: string
    }
  | {
      connection_name: string
      resource: string
    }

export type GrantsAndAllowedApis = {
  grants: Array<Grant>
  apis: Array<TokenAllowedApis>
}

export type WorkflowEnvVars = {
  analyticsToolboxDataset?: string
  analyticsToolboxVersion?: string
  apiBaseUrl?: string
  accessToken?: string
  dataExportDefaultGCSBucket?: string
  bigqueryProjectId?: string
  bigqueryRegion?: string
  isCartoDW?: boolean
  domain?: string
  mapProvider?: {
    geocoding: string
    isolines: string
    routing: string
  }
}

export type QueryAndOutputs = {
  query: string
  outputs: Record<string, string>
}

export type ComponentQuery = {
  componentName: string
  query: string
  outputs: Record<string, string>
}

export enum WorkflowComponentParamType {
  InputTable = 'Table',
  OutputTable = 'OutputTable',
  Column = 'Column',
  Number = 'Number',
  String = 'String',
  StringSql = 'StringSql',
  Boolean = 'Boolean',
  Selection = 'Selection',
  SelectionType = 'SelectionType',
  Json = 'Json',
  GeoJson = 'GeoJson',
  GeoJsonDraw = 'GeoJsonDraw',
  Condition = 'Condition',
  Range = 'Range',
  ColumnNumber = 'ColumnNumber',
  SelectColumnNumber = 'SelectColumnNumber',
  SelectColumnType = 'SelectColumnType',
  SelectColumnAggregation = 'SelectColumnAggregation',
  Email = 'Email',
  ColumnsForJoin = 'ColumnsForJoin',
  JsonExtractPaths = 'JsonExtractPaths'
}

export enum WorkflowComponentParameterMode {
  Radio = 'radio',
  Slider = 'slider',
  Toggle = 'toggle',
  Multiple = 'multiple',
  Multiline = 'multiline'
}

export enum WorkflowCodeType {
  Full = 'Full',
  FullNoCache = 'FullNoCache',
  DryRun = 'DryRun',
  StoredProcedure = 'StoredProcedure'
}

export enum LocationType {
  Temp = 'temp',
  Workspace = 'workspace',
  Extensions = 'extensions'
}

export type WorkflowComponentMetadata = {
  name: string
  group: string
  title: string
  description: string
  tags?: Array<string>
  starred?: boolean
  deprecated?: boolean
  icon?: string // a BASE64 encoded icon
  warningTitle?: string
  warningDescription?: string
  warningShowIf?: { parameter: string; value: string | boolean | number }[]
}

export type ExtensionDefinition = {
  name: string
  version: string
  components: Array<ExtensionComponentDefinition>
}

export type ExtensionComponentDefinition = {
  name: string
  title: string
  description: string
  group: string
  version: string
  inputs: WorkflowComponentInput[]
  outputs: WorkflowComponentOutput[]
  procedureName: string
  icon?: string
  cartoEnvVars?: Array<string>
}

export enum WorkflowComponentOutputType {
  Table = 'Table',
  View = 'View'
}

export type WorkflowComponentOutput = {
  name: string
  title: string
  description: string
  type: WorkflowComponentOutputType
}

export type InputCondition = {
  parameter: string
  value: Primitive
}

export type WorkflowComponentInput = {
  name: string
  type: WorkflowComponentParamType
  title: string
  description?: string
  optional?: boolean
  advanced?: boolean
  validation?: string
  providers?: Array<Provider>
  allowExpressions?: boolean
  requiredAnalyticsToolboxVersion?: Partial<Record<Provider, string>>
  [others: string]: unknown
}

export type WorkflowComponentInputNumber = WorkflowComponentInput & {
  min?: number
  max?: number
}

export type WorkflowComponentInputSelection = WorkflowComponentInput & {
  options: Array<string>
}

/**
 * The type of a value used as a component parameter, defining its origin
 */

export enum WorkflowComponentValueType {
  Node = 'Node',
  Literal = 'Literal'
}

export type Coordinates = {
  x: number
  y: number
}

export enum WorkflowComponentIconTypes {
  Default = 'Default',
  Success = 'Success',
  Failure = 'Failure',
  Executed = 'Executed',
  Configured = 'Configured'
}

export type WorkflowComponentDescription = {
  name: string
  group: string
  title: string
  description: string
  inputs: WorkflowComponentInput[]
  outputs: WorkflowComponentOutput[]
  [other: string]: unknown
}

export type OutputNodeDefinition = {
  component: string
  output?: string | null
}

export type Primitive = string | boolean | number | null

export type WorkflowComponentValueDataType = Primitive | Array<Primitive> | OutputNodeDefinition

export type WorkflowComponentValue = {
  type: WorkflowComponentValueType
  value: WorkflowComponentValueDataType
}

/*
 * keys: names of the parameters of the component
 * values: type/values of the parameters of the component
 */
export type WorkflowComponentValues = Record<string, WorkflowComponentValue | Array<WorkflowComponentValue>>

export type WorkflowParameterValueError = {
  parameter: string
  type: WorkflowParameterValueErrorType
  message: string
}

export enum WorkflowParameterValueErrorType {
  WrongValue = 'wrongvalue',
  MissingValue = 'missingvalue'
}

export type WorkflowProblem = {
  message: string
}

export type WorkflowComponentProblems = {
  componentName: string | null
  problems: Array<WorkflowParameterValueError>
}

export type WorkflowValidity = {
  valid: boolean
  problems?: Array<WorkflowComponentProblems | WorkflowProblem>
}

type WorkflowEdge = {
  id: string
  type?: string
  source: string
  target: string
  sourceHandle?: string | null
  targetHandle?: string | null
  data?: Record<string, unknown>
}

export type DOSourceVariable = {
  name: string
  type: string
  dbType: string
  method: string
}

export type DOSourceDefinition = {
  query: string
  variables: DOSourceVariable[] | null
}

export type WorkflowNode = {
  id: string
  data: {
    name: string
    label?: string
    inputs: {
      name: string
      value?: string | boolean | number | string[]
    }[]
    version?: string
    variables?: DOSourceVariable[]
  }
  type?: string
  className?: string
}

export type WorkflowDiagram = {
  schemaVersion: string
  connectionProvider: Provider
  thumbnailUrl: string
  title: string
  description: string
  tags: Array<string>
  nodes: Array<WorkflowNode>
  edges: Array<WorkflowEdge>
  variables?: WorkflowVariable[]
  executionInfo?: Record<string, unknown>
}

export enum ColumnDataType {
  Number = 'number',
  String = 'string',
  Boolean = 'boolean',
  Geometry = 'geometry',
  Geography = 'geography',
  Date = 'date',
  Time = 'time',
  Datetime = 'datetime',
  Timestamp = 'timestamp',
  Binary = 'binary'
}

export type TableColumnDefinition = {
  name: string
  type: ColumnDataType
  db_type?: string
}

export type TableSchema = Array<TableColumnDefinition>

/**
 * Expected table schemas for the tables used as input or generated as output by a workflow component.
 */
export type TableSchemas = Array<{
  name: string // name of the input or output associated to the schema
  fqn: string
  schema: TableSchema | null
}>

export type WorkflowTableSchemas = Array<{
  componentName: string
  schemas: TableSchemas
}>

export type TableSchemasList = Array<{
  fqn: string
  schema: TableSchema | null
}>

export type ComponentInputSchemas = Record<string, TableSchema | Array<TableSchema>>
export type ComponentOutputSchemas = Record<string, TableSchema>

export type ComponentInputOutputSchemas = {
  input: ComponentInputSchemas
  output: ComponentOutputSchemas
}

export enum TableOptimizationColumnDataType {
  NonGeo = 'nongeo',
  Geo = 'geo'
}

export type TableOptimizationColumn = {
  name: string
  type: TableOptimizationColumnDataType
}

export type TableOptimizationInfo = {
  column: TableOptimizationColumn | null
  pgCluster?: boolean
  sfSearchOptimizationColumn?: TableOptimizationColumn | null
}

export type WorkflowComponentCodeParams = {
  workflow: Workflow
  params: Record<string, unknown>
  connection: DataWarehouseConnection
  envVars: WorkflowEnvVars
  componentName: string
  codeType: WorkflowCodeType
  schemas?: ComponentInputOutputSchemas
  tablename?: string
  viewname?: string
}

export type GetOutputSchemasParams = {
  workflow: Workflow
  connection: DataWarehouseConnection
  params: Record<string, unknown>
  componentName: string
  envVars: WorkflowEnvVars
  schemas: TableSchemasList
}

export type GetOutputSchemaParams = {
  connection: DataWarehouseConnection
  params: Record<string, unknown>
  columns: Record<string, TableSchema>
  envVars: WorkflowEnvVars
}

export type WorkflowComponentCheckInputValuesParams = {
  params: WorkflowComponentValues
  resolvedParamValues: Record<string, unknown>
  connection: DataWarehouseConnection
  dryRun?: boolean
  schemas?: WorkflowTableSchemas
  variables?: WorkflowVariable[]
}

export type WorkflowComponentOutputTablesParams = {
  workflow: Workflow
  params: Record<string, unknown>
  connection: DataWarehouseConnection
  componentName: string
  codeType: WorkflowCodeType
}

export type WorkflowComponentGetRequiredGrantsParams = {
  workflow: Workflow
  connection: DataWarehouseConnection
  params: Record<string, unknown>
  componentName: string
}

export type ExecuteDynamicSQLParams = {
  sql: string
  addQuotes: boolean
  replacements?: Array<[string, string]>
}

export type ResolveParamValuesParams = {
  connection: DataWarehouseConnection
  codeType: WorkflowCodeType
  schemas?: WorkflowTableSchemas
  workflowParams?: Record<string, WorkflowComponentValues>
}

export type WorkflowComponentConditionCodeParams = {
  workflow: Workflow
  envVars: WorkflowEnvVars
  componentName: string
  connection: DataWarehouseConnection
  params: Record<string, unknown>
  ifQuery: string
  elseQuery: string
  codeType: WorkflowCodeType
  schemas: WorkflowTableSchemas
}

export type StoredProcedureComponentError = {
  storedProcedureCode: string
  error: string
}

export type ExtensionComponentError = {
  extensionName: string
  componentName: string | null
  error: string
}

export type WorkflowComponentsFromStoredProcedures = {
  validComponents: Array<StoredProcedureWorkflowComponent>
  errors: Array<StoredProcedureComponentError>
}

export type WorkflowComponentsFromExtensions = {
  validComponents: Array<ExtensionWorkflowComponent>
  errors: Array<ExtensionComponentError>
}

export type WorkflowVariable = {
  name: string
  type: WorkflowComponentParamType.Number | WorkflowComponentParamType.String
  scopes: StoredProcedureScope[]
  value: string
  order: number
  displayName?: string
  helperText?: string
}

export enum ConditionType {
  Equal = '=',
  NotEqual = '!=',
  GreaterThan = '>',
  GreaterThanOrEqual = '>=',
  LessThan = '<',
  LessThanOrEqual = '<=',
  Contains = 'contains',
  Null = 'IS NULL',
  True = 'IS TRUE',
  False = 'IS FALSE'
}

export type Conditions = {
  condition?: ConditionType
  value: string
  return: string
}

export type ColumnConditions = { column: string; conditions?: Conditions[] }

export type WorkflowTask = {
  run_if: string
  task_key: string
  depends_on: Array<{ task_key: string }>
  notebook_task: {
    notebook_path: string
    language: string
    content: string // base64-encoded
  }
}

export type WorkflowAsTasks = {
  tasks: WorkflowTask[]
  name: string
}

export enum IsolinesProviders {
  Here = 'here',
  Mapbox = 'mapbox',
  Mock = 'mock',
  TomTom = 'tomtom',
  TravelTime = 'traveltime'
}

export enum RoutingMatrixProviders {
  Mock = 'mock',
  TomTom = 'tomtom',
  TravelTime = 'traveltime'
}

export enum StoredProcedureScope {
  Api = 'api',
  McpTool = 'mcptool',
  Viewer = 'viewer'
}

export interface JsonExtractPath {
  jsoncolumn: string
  path: string
  newcol: string
}

export enum NumericType {
  Float = 'float',
  Integer = 'integer'
}
